import { faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment/moment";
import React, {
  type Dispatch,
  type FC,
  Fragment,
  type SetStateAction,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useApiCall } from "../api/api";
import type { Company, Exam, ExamParticipant, Project } from "../api/dto";
import { SectionDivider } from "../components/content/SectionDivider";
import { SectionHeader } from "../components/content/SectionHeader";
import { CheckBox } from "../components/form/CheckBox";
import { Input } from "../components/form/Input";
import { ModalFooter } from "../components/layout/ModalProvider";
import { useRefresh } from "../components/RefreshController";
import {
  getDashboardDataFileName,
  getDataReportDataFileName,
} from "../util/getOverallDataFileName";

interface ExportManagementDataReportModalProps {
  allCompanies: Company[];
  allProjects: Project[];
  allExams: Exam[];
  allParticipants: ExamParticipant[];
  company: Company;
  onlyExams: Exam["id"][];
  onlyContractors: ExamParticipant["contractor"][] | null;
  allContractors: ExamParticipant["contractor"][];
  onlySubContractors: ExamParticipant["subContractor"][] | null;
  onlyParticipants: ExamParticipant["id"][];
}

export const ExportManagementDataReportModal: FC<ExportManagementDataReportModalProps> = (
  props,
) => {
  const { exportManagementDataReport } = useApiCall();
  const [groupedContractors, setGroupedContractors] = useState<string[]>([]);
  const exams = useMemo(() => {
    return props.allExams.filter((exam) => props.onlyExams.includes(exam.id));
  }, [props.allExams, props.onlyExams]);
  const examDays = Array.from(new Set(exams.map((e) => moment(e.examAt).format("YYYY-MM-DD"))));

  const exportAndDownload = async () => {
    const contractorsFilter =
      props.onlyContractors !== null && props.onlyContractors.length > 0
        ? props.onlyContractors
        : null;
    const subContractorsFilter =
      props.onlySubContractors !== null && props.onlySubContractors.length > 0
        ? props.onlySubContractors
        : null;
    const submitContractors = (
      props.onlyContractors ??
      Array.from(
        new Set(
          props.allParticipants
            .filter((p) => props.onlyExams.includes(p.examId))
            .map((p) => p.contractor),
        ),
      )
    ).filter((c) => contractors.includes(c));
    const blob = await exportManagementDataReport(
      enabledPages,
      coverPhoto,
      props.onlyExams,
      Array.from(new Set(groupedContractors)),
      submitContractors,
      subContractorsFilter,
      props.onlyParticipants,
    );
    const file = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = file;
    a.download = getDataReportDataFileName(exams);
    a.click();
    URL.revokeObjectURL(file);
  };

  const [contractors, setContractors] = useState<string[]>([]);
  // shows all contractors on exam(s), no matter if they are selected beforehand or not.
  const allContractors = useMemo(() => {
    const matchingParticipants: ExamParticipant[] = [];
    exams.forEach((exam) => {
      const newAddition = props.allParticipants.filter((ap) => ap.examId === exam.id);
      matchingParticipants.push(...newAddition);
    });
    const contractors = Array.from(new Set(matchingParticipants.map((c) => c.contractor)));
    setContractors(contractors);
    return contractors;
  }, [exams]);

  const [coverPhoto, setCoverPhoto] = useState<File | null>(null);

  const availableConfigurationSteps = {
    cover_page: <CoverPageConfigurationStep setCoverPhoto={setCoverPhoto} />,
    introduction: null,
    dijkgraaf_support_bv: null,
    overall_management_dashboard: (
      <OverallManagementDashboardConfigurationStep
        contractors={contractors}
        groupedContractors={groupedContractors}
        setGroupedContractors={setGroupedContractors}
      />
    ),
    dashboard_and_scores_per_contractor: null,
    management_dashboard_per_contractor: null,
    used_score_card: null,
    used_instruction_card: null,
    test_results_per_person: null,
    filled_in_score_card: null,
    back_cover: null,
  };

  const [enabledPages, setEnabledPages] = useState<string[]>(
    Object.keys(availableConfigurationSteps).filter(
      (x) => x !== "dashboard_and_scores_per_contractor",
    ),
  );

  return (
    <div>
      <div className={"flex items-stretch -mb-6 -mr-4"}>
        <div
          className={
            "w-96 border-r border-slate-100 h-[calc(100vh-14rem)] overflow-y-scroll py-3 -mt-3 mb-3  flex flex-col space-y-3"
          }
        >
          <SidebarItem
            name={"Front Cover"}
            page={"cover_page"}
            pages={Object.keys(availableConfigurationSteps)}
            enabledPages={enabledPages}
            setEnabledPages={setEnabledPages}
          />
          <SidebarItem
            name={"Introduction"}
            page={"introduction"}
            pages={Object.keys(availableConfigurationSteps)}
            enabledPages={enabledPages}
            setEnabledPages={setEnabledPages}
          />
          <SidebarItem
            name={"Dijkgraaf-Support BV"}
            page={"dijkgraaf_support_bv"}
            pages={Object.keys(availableConfigurationSteps)}
            enabledPages={enabledPages}
            setEnabledPages={setEnabledPages}
          />
          <SidebarItem
            name={"Overall Management Dashboard"}
            page={"overall_management_dashboard"}
            pages={Object.keys(availableConfigurationSteps)}
            enabledPages={enabledPages}
            setEnabledPages={setEnabledPages}
          />
          <SidebarItem
            name={"Management Dashboard per Contractor"}
            page={"management_dashboard_per_contractor"}
            pages={Object.keys(availableConfigurationSteps)}
            enabledPages={enabledPages}
            setEnabledPages={setEnabledPages}
          />
          <SidebarItem
            name={"Management Dashboard, Results and Scorecards per Contractor"}
            page={"dashboard_and_scores_per_contractor"}
            pages={Object.keys(availableConfigurationSteps)}
            enabledPages={enabledPages}
            setEnabledPages={setEnabledPages}
          />
          <SidebarItem
            name={"Used Scorecard"}
            page={"used_score_card"}
            pages={Object.keys(availableConfigurationSteps)}
            enabledPages={enabledPages}
            setEnabledPages={setEnabledPages}
          />
          <SidebarItem
            name={"Used Instruction Card"}
            page={"used_instruction_card"}
            pages={Object.keys(availableConfigurationSteps)}
            enabledPages={enabledPages}
            setEnabledPages={setEnabledPages}
          />
          <SidebarItem
            name={"Test Results per Person"}
            page={"test_results_per_person"}
            pages={Object.keys(availableConfigurationSteps)}
            enabledPages={enabledPages}
            setEnabledPages={setEnabledPages}
          />
          <SidebarItem
            name={"Filled in Scorecard"}
            page={"filled_in_score_card"}
            pages={Object.keys(availableConfigurationSteps)}
            enabledPages={enabledPages}
            setEnabledPages={setEnabledPages}
          />
          <SidebarItem
            name={"Back Cover"}
            page={"back_cover"}
            pages={Object.keys(availableConfigurationSteps)}
            enabledPages={enabledPages}
            setEnabledPages={setEnabledPages}
          />
        </div>
        <div
          className={
            "flex-1 h-[calc(100vh-14rem)] overflow-y-scroll py-3 -mt-3 mb-3 px-4 flex flex-col space-y-3"
          }
        >
          <FilterContractorsConfigurationStep
            allContractors={allContractors}
            contractors={contractors}
            setContractors={setContractors}
          />
          {enabledPages.map((page, i) => {
            return (
              <div key={i}>
                {availableConfigurationSteps[page as keyof typeof availableConfigurationSteps] ?? (
                  <></>
                )}
              </div>
            );
          })}
        </div>
      </div>
      <ModalFooter text={"Exporteren"} onSubmit={exportAndDownload} />
    </div>
  );
};

const SidebarItem: FC<{
  name: string;
  page: string;
  pages: string[];
  enabledPages: string[];
  setEnabledPages: Dispatch<SetStateAction<string[]>>;
}> = (props) => {
  const enabled = props.enabledPages.includes(props.page);
  const enable = () =>
    props.setEnabledPages((old) => {
      const newPages = [...old, props.page];
      return props.pages.filter((x) => newPages.includes(x));
    });
  const disable = () => props.setEnabledPages((old) => old.filter((p) => p !== props.page));
  return (
    <div
      className={`min-h-20 flex items-center border border-slate-200 hover:bg-brand-50 hover:border-brand-700 cursor-pointer mr-3 rounded-lg`}
      onClick={(e) => {
        e.stopPropagation();
        if (enabled) {
          disable();
        } else {
          enable();
        }
      }}
    >
      <CheckBox
        state={enabled ? "checked" : "unchecked"}
        onSelect={() => enable()}
        onDeselect={() => disable()}
      />
      <div className={"mx-3 flex-1"}>
        <strong>{props.name}</strong>
      </div>
    </div>
  );
};

const FilterContractorsConfigurationStep: FC<{
  contractors: string[];
  allContractors: string[];
  setContractors: Dispatch<SetStateAction<string[]>>;
}> = (props) => {
  return (
    <div>
      <SectionHeader>Algemeen</SectionHeader>
      <div className={"text-sm text-slate-600"}>
        Rapport maken voor enkele contractor(s)? Selecteer de contractors hieronder om het enkel
        voor hun te maken.
      </div>
      <div className={"grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-2 mt-2"}>
        {props.allContractors.map((contractor, i) => (
          <label
            key={i}
            className={`flex items-center h-10 px-2 border rounded-sm cursor-pointer text-sm leading-tight ${props.contractors.includes(contractor) ? "bg-brand-50 border-brand-500" : "border-slate-200"}`}
          >
            <input
              type={"checkbox"}
              className={"mr-2"}
              checked={props.contractors.includes(contractor)}
              onChange={(e) => {
                if (e.target.checked) {
                  props.setContractors([...props.contractors, contractor]);
                } else {
                  props.setContractors(props.contractors.filter((c) => c !== contractor));
                }
              }}
            />
            <span>{contractor}</span>
          </label>
        ))}
      </div>
    </div>
  );
};

const CoverPageConfigurationStep: FC<{ setCoverPhoto: Dispatch<SetStateAction<File | null>> }> = (
  props,
) => {
  return (
    <div>
      <SectionHeader>Front Cover</SectionHeader>
      <input
        type={"file"}
        multiple={false}
        accept={"image/*"}
        onChange={(event) => {
          if (event.target.files?.length) {
            props.setCoverPhoto(event.target.files[0]);
          }
        }}
      />
    </div>
  );
};
const OverallManagementDashboardConfigurationStep: FC<{
  contractors: string[];
  groupedContractors: string[];
  setGroupedContractors: Dispatch<SetStateAction<string[]>>;
}> = (props) => {
  return (
    <div>
      <SectionHeader>Overall Management Dashboard</SectionHeader>
      <div className={"text-sm text-slate-600"}>
        Veel kleinere contractors? Groepeer de kleine onder de naam <strong>"Other"</strong>.
        Selecteer contractors om te groeperen.
      </div>
      <div className={"grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-2 mt-2"}>
        {props.contractors.map((contractor, i) => (
          <label
            key={i}
            className={`flex items-center h-10 px-2 border rounded-sm cursor-pointer text-sm leading-tight ${props.groupedContractors.includes(contractor) ? "bg-brand-50 border-brand-500" : "border-slate-200"}`}
          >
            <input
              type={"checkbox"}
              className={"mr-2"}
              checked={props.groupedContractors.includes(contractor)}
              onChange={(e) => {
                if (e.target.checked) {
                  props.setGroupedContractors([...props.groupedContractors, contractor]);
                } else {
                  props.setGroupedContractors(
                    props.groupedContractors.filter((c) => c !== contractor),
                  );
                }
              }}
            />
            <span>{contractor}</span>
          </label>
        ))}
      </div>
    </div>
  );
};
